/**
 * FE-006: WebRTC Peer Connection Management
 *
 * Manages an RTCPeerConnection for a single matched session.
 * Provides reactive SolidJS signals for connection state and remote streams.
 * Implements trickle ICE, SDP munging, host candidate filtering (NF-33),
 * and stage-based video track replacement.
 */

import { createSignal, batch } from "solid-js";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type PeerConnectionState =
  | "new"
  | "connecting"
  | "connected"
  | "disconnected"
  | "failed";

export interface PeerConnectionConfig {
  iceServers: RTCIceServer[];
  /** 'all' (default) or 'relay' for full privacy mode (NF-33). */
  iceTransportPolicy?: RTCIceTransportPolicy;
  /** If true, strip host ICE candidates (default true for IP privacy). */
  stripHostCandidates?: boolean;
}

export interface ConnectionStats {
  usingRelay: boolean;
  localCandidateType: string | null;
  remoteCandidateType: string | null;
  bytesSent: number;
  bytesReceived: number;
  currentBitrate: number;
  timestamp: number;
}

// Callback type for signaling integration
export type OnIceCandidateCallback = (candidate: RTCIceCandidate) => void;

// ---------------------------------------------------------------------------
// SDP Munging Helpers
// ---------------------------------------------------------------------------

/**
 * Reorder codec preferences in SDP to favor specific codecs.
 * Moves VP8/VP9 to top for video, Opus for audio.
 */
export function mungeCodecPreferences(sdp: string): string {
  const lines = sdp.split("\r\n");
  const result: string[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Detect media sections and reorder codec preferences
    if (line.startsWith("m=")) {
      if (line.startsWith("m=audio")) {
        result.push(reorderMediaLine(line, lines, "opus"));
        continue;
      } else if (line.startsWith("m=video")) {
        result.push(reorderMediaLine(line, lines, "VP8", "VP9"));
        continue;
      }
    }

    result.push(line);
  }

  return result.join("\r\n");
}

/**
 * Reorder payload types in an m= line to prioritize the given codec names.
 */
function reorderMediaLine(
  mLine: string,
  allLines: string[],
  ...preferredCodecs: string[]
): string {
  // m=audio 9 UDP/TLS/RTP/SAVPF 111 103 104 ...
  const parts = mLine.split(" ");
  if (parts.length < 4) return mLine;

  const payloadTypes = parts.slice(3);

  // Build a map from payload type -> codec name (from rtpmap lines)
  const codecMap = new Map<string, string>();
  for (const line of allLines) {
    const match = line.match(/^a=rtpmap:(\d+)\s+([^/]+)/);
    if (match) {
      codecMap.set(match[1], match[2]);
    }
  }

  // Partition: preferred codecs first, then the rest
  const preferred: string[] = [];
  const rest: string[] = [];

  for (const pt of payloadTypes) {
    const codecName = codecMap.get(pt)?.toLowerCase() ?? "";
    const isPreferred = preferredCodecs.some(
      (c) => codecName === c.toLowerCase(),
    );
    if (isPreferred) {
      preferred.push(pt);
    } else {
      rest.push(pt);
    }
  }

  const reordered = [...preferred, ...rest];
  return [...parts.slice(0, 3), ...reordered].join(" ");
}

/**
 * Filter host ICE candidates from an SDP string.
 * Removes a=candidate lines that contain "typ host".
 */
export function stripHostCandidatesFromSdp(sdp: string): string {
  return sdp
    .split("\r\n")
    .filter((line) => {
      if (line.startsWith("a=candidate:")) {
        return !line.includes(" typ host ");
      }
      return true;
    })
    .join("\r\n");
}

// ---------------------------------------------------------------------------
// Peer Connection Service
// ---------------------------------------------------------------------------

export function createPeerConnection(config: PeerConnectionConfig) {
  // -- Reactive state -------------------------------------------------------
  const [state, setState] = createSignal<PeerConnectionState>("new");
  const [remoteStream, setRemoteStream] = createSignal<MediaStream | null>(null);
  const [stats, setStats] = createSignal<ConnectionStats>({
    usingRelay: false,
    localCandidateType: null,
    remoteCandidateType: null,
    bytesSent: 0,
    bytesReceived: 0,
    currentBitrate: 0,
    timestamp: 0,
  });

  // -- Internal state -------------------------------------------------------
  let pc: RTCPeerConnection | null = null;
  let onIceCandidateCb: OnIceCandidateCallback | null = null;
  let onRemoteStreamCb: ((stream: MediaStream | null) => void) | null = null;
  let onStateChangeCb: ((state: PeerConnectionState) => void) | null = null;
  let statsTimer: ReturnType<typeof setInterval> | null = null;
  let prevBytesReceived = 0;
  let prevTimestamp = 0;

  const stripHost = config.stripHostCandidates ?? true;

  // -- Create RTCPeerConnection ---------------------------------------------

  function createConnection(): RTCPeerConnection {
    const rtcConfig: RTCConfiguration = {
      iceServers: config.iceServers,
      iceTransportPolicy: config.iceTransportPolicy ?? "all",
      bundlePolicy: "max-bundle",
    };

    pc = new RTCPeerConnection(rtcConfig);

    // Connection state monitoring
    pc.onconnectionstatechange = () => {
      if (!pc) return;
      const rtcState = pc.connectionState;
      console.log('[peer] connectionState:', rtcState);
      let newState: PeerConnectionState;
      switch (rtcState) {
        case "new":
          newState = "new";
          break;
        case "connecting":
          newState = "connecting";
          break;
        case "connected":
          newState = "connected";
          startStatsPolling();
          break;
        case "disconnected":
          newState = "disconnected";
          break;
        case "failed":
          newState = "failed";
          stopStatsPolling();
          break;
        case "closed":
          newState = "disconnected";
          stopStatsPolling();
          break;
        default:
          newState = "new";
          break;
      }
      setState(newState);
      onStateChangeCb?.(newState);
    };

    // ICE connection state monitoring (more granular than connectionState)
    pc.oniceconnectionstatechange = () => {
      if (!pc) return;
      console.log('[peer] iceConnectionState:', pc.iceConnectionState);
    };

    // ICE gathering state
    pc.onicegatheringstatechange = () => {
      if (!pc) return;
      console.log('[peer] iceGatheringState:', pc.iceGatheringState);
    };

    // ICE candidate handling
    pc.onicecandidate = (event) => {
      if (event.candidate) {
        // Filter host candidates for IP privacy (NF-33)
        if (stripHost && event.candidate.type === "host") {
          return;
        }
        onIceCandidateCb?.(event.candidate);
      }
    };

    // Remote track handling
    pc.ontrack = (event) => {
      const stream = event.streams[0] ?? new MediaStream([event.track]);
      setRemoteStream(stream);
      onRemoteStreamCb?.(stream);
    };

    return pc;
  }

  // -- Caller path ----------------------------------------------------------

  async function createOffer(): Promise<RTCSessionDescriptionInit> {
    if (!pc) throw new Error("PeerConnection not created");

    setState("connecting");
    const offer = await pc.createOffer({
      offerToReceiveAudio: true,
      offerToReceiveVideo: true,
    });

    // SDP munging: reorder codecs
    if (offer.sdp) {
      offer.sdp = mungeCodecPreferences(offer.sdp);
      if (stripHost) {
        offer.sdp = stripHostCandidatesFromSdp(offer.sdp);
      }
    }

    await pc.setLocalDescription(offer);
    return offer;
  }

  async function handleAnswer(sdp: RTCSessionDescriptionInit): Promise<void> {
    if (!pc) throw new Error("PeerConnection not created");
    await pc.setRemoteDescription(new RTCSessionDescription(sdp));
  }

  // -- Callee path ----------------------------------------------------------

  async function handleOffer(
    sdp: RTCSessionDescriptionInit,
  ): Promise<RTCSessionDescriptionInit> {
    if (!pc) throw new Error("PeerConnection not created");

    setState("connecting");
    await pc.setRemoteDescription(new RTCSessionDescription(sdp));

    const answer = await pc.createAnswer();

    // SDP munging: reorder codecs
    if (answer.sdp) {
      answer.sdp = mungeCodecPreferences(answer.sdp);
      if (stripHost) {
        answer.sdp = stripHostCandidatesFromSdp(answer.sdp);
      }
    }

    await pc.setLocalDescription(answer);
    return answer;
  }

  // -- ICE management -------------------------------------------------------

  async function addIceCandidate(candidate: RTCIceCandidateInit): Promise<void> {
    if (!pc) throw new Error("PeerConnection not created");
    await pc.addIceCandidate(new RTCIceCandidate(candidate));
  }

  function onIceCandidate(callback: OnIceCandidateCallback): void {
    onIceCandidateCb = callback;
  }

  function onRemoteStream(callback: (stream: MediaStream | null) => void): void {
    onRemoteStreamCb = callback;
  }

  function onStateChange(callback: (state: PeerConnectionState) => void): void {
    onStateChangeCb = callback;
  }

  /**
   * Restart ICE on connection failure. Creates a new offer with iceRestart.
   */
  async function restartIce(): Promise<RTCSessionDescriptionInit | null> {
    if (!pc) return null;

    const offer = await pc.createOffer({ iceRestart: true });
    if (offer.sdp) {
      offer.sdp = mungeCodecPreferences(offer.sdp);
      if (stripHost) {
        offer.sdp = stripHostCandidatesFromSdp(offer.sdp);
      }
    }
    await pc.setLocalDescription(offer);
    setState("connecting");
    return offer;
  }

  // -- Track management -----------------------------------------------------

  function addLocalAudioTrack(track: MediaStreamTrack): RTCRtpSender | null {
    if (!pc) throw new Error("PeerConnection not created");
    return pc.addTrack(track);
  }

  /**
   * Replace the video track on the existing sender.
   * - null: Stage 1 (audio-only, remove video)
   * - filtered track: Stage 2 (filtered video)
   * - raw track: Stage 3 (clear video)
   *
   * Uses RTCRtpSender.replaceTrack() for seamless switching without renegotiation.
   */
  async function replaceVideoTrack(
    track: MediaStreamTrack | null,
  ): Promise<void> {
    if (!pc) throw new Error("PeerConnection not created");

    const senders = pc.getSenders();
    const videoSender = senders.find((s) => s.track?.kind === "video" || (!s.track && s));

    if (videoSender) {
      await videoSender.replaceTrack(track);
    } else if (track) {
      // No existing video sender — add the track
      pc.addTrack(track);
    }
  }

  // -- Stats polling --------------------------------------------------------

  function startStatsPolling(): void {
    stopStatsPolling();
    statsTimer = setInterval(async () => {
      if (!pc) return;

      try {
        const report = await pc.getStats();
        let localCandidateType: string | null = null;
        let remoteCandidateType: string | null = null;
        let bytesSent = 0;
        let bytesReceived = 0;
        let usingRelay = false;

        report.forEach((stat) => {
          if (stat.type === "candidate-pair" && stat.state === "succeeded") {
            // Look up the candidate types
            const localCandidate = report.get(stat.localCandidateId);
            const remoteCandidate = report.get(stat.remoteCandidateId);
            if (localCandidate) {
              localCandidateType = localCandidate.candidateType ?? null;
              usingRelay = localCandidate.candidateType === "relay";
            }
            if (remoteCandidate) {
              remoteCandidateType = remoteCandidate.candidateType ?? null;
            }
            bytesSent = stat.bytesSent ?? 0;
            bytesReceived = stat.bytesReceived ?? 0;
          }
        });

        const now = Date.now();
        const elapsed = prevTimestamp > 0 ? (now - prevTimestamp) / 1000 : 0;
        const bitrate =
          elapsed > 0
            ? ((bytesReceived - prevBytesReceived) * 8) / elapsed
            : 0;

        prevBytesReceived = bytesReceived;
        prevTimestamp = now;

        setStats({
          usingRelay,
          localCandidateType,
          remoteCandidateType,
          bytesSent,
          bytesReceived,
          currentBitrate: Math.max(0, bitrate),
          timestamp: now,
        });
      } catch {
        // Stats collection failed, not critical
      }
    }, 2000);
  }

  function stopStatsPolling(): void {
    if (statsTimer) {
      clearInterval(statsTimer);
      statsTimer = null;
    }
  }

  // -- Cleanup --------------------------------------------------------------

  function close(): void {
    stopStatsPolling();

    if (pc) {
      // Stop all remote tracks
      const remote = remoteStream();
      if (remote) {
        remote.getTracks().forEach((track) => track.stop());
      }

      // Remove all senders
      try {
        pc.getSenders().forEach((sender) => {
          try {
            pc!.removeTrack(sender);
          } catch {
            // May fail if already closed
          }
        });
      } catch {
        // ignore
      }

      pc.onconnectionstatechange = null;
      pc.onicecandidate = null;
      pc.ontrack = null;
      pc.close();
      pc = null;
    }

    batch(() => {
      setState("disconnected");
      setRemoteStream(null);
    });

    onIceCandidateCb = null;
    onRemoteStreamCb = null;
    onStateChangeCb = null;
    prevBytesReceived = 0;
    prevTimestamp = 0;
  }

  // -- Public API -----------------------------------------------------------

  return {
    // Reactive state
    state,
    remoteStream,
    stats,

    // Connection lifecycle
    createConnection,
    close,

    // Caller path
    createOffer,
    handleAnswer,

    // Callee path
    handleOffer,

    // ICE
    addIceCandidate,
    onIceCandidate,
    restartIce,

    // Callbacks
    onRemoteStream,
    onStateChange,

    // Tracks
    addLocalAudioTrack,
    replaceVideoTrack,

    // Internal (for testing)
    get _pc() {
      return pc;
    },
  };
}

export type PeerConnection = ReturnType<typeof createPeerConnection>;
